// onboardingFields.ts
// Phase 5 (2026-05-25) — schema/config for which onboarding fields are
// enabled for each account status. Drives both the field-visibility UI
// and the backend validation guard (staged in Phase 5 backend module).
//
// Hard rules per WO:
//   - Demo mode MUST NOT collect SSN, Tax ID, DOB, full residential address,
//     marital status, dependents, employer name, job title, etc.
//   - The locked-field copy MUST read exactly:
//       "Available when real-account onboarding is enabled. Demo and
//        paper-only accounts do not require this information."
//   - Real-account onboarding fields can be enabled later with explicit
//     compliance approval (Phase 17+).

import type { UserStatus } from '../hooks/useAuthenticatedUser';

export type FieldState = 'enabled' | 'optional' | 'disabled' | 'hidden';

export interface FieldSpec {
  id: string;
  label: string;
  /** Whether the field is enabled / optional / disabled / hidden in each status. Optional — if omitted, defaultState applies for every status. */
  states?: Partial<Record<UserStatus, FieldState>>;
  /** Default state when status not listed in `states`. */
  defaultState: FieldState;
  /** Internal note — never rendered. */
  note?: string;
}

export const LOCKED_FIELD_COPY =
  'Available when real-account onboarding is enabled. Demo and paper-only accounts do not require this information.';

// Demo-enabled fields (per Phase 5 WO):
//   first name, last name, email, password, phone (optional), country,
//   state, employment status (optional), investor type, trading experience,
//   financial comfort ranges, strategy builder inputs, disclosures,
//   SMS preference (optional), trusted contact (optional).
//
// Disabled/grayed/hidden for Demo (and paper-only until real-account
// onboarding is enabled):
//   SSN, Tax ID, DOB, marital status, dependents, backup tax withholding,
//   full residential address, employer name, job title, real brokerage
//   agreements, live crypto enablement, entity/joint/retirement account
//   opening.

export const ONBOARDING_FIELDS: FieldSpec[] = [
  // ---- Demo-enabled ----
  { id: 'first_name',          label: 'First name',                  defaultState: 'enabled' },
  { id: 'last_name',           label: 'Last name',                   defaultState: 'enabled' },
  { id: 'email',               label: 'Email',                       defaultState: 'enabled' },
  { id: 'password',            label: 'Password',                    defaultState: 'enabled' },
  { id: 'phone',               label: 'Phone',                       defaultState: 'optional' },
  { id: 'country',             label: 'Country',                     defaultState: 'enabled' },
  { id: 'state_region',        label: 'State / Region',              defaultState: 'enabled' },
  { id: 'employment_status',   label: 'Employment status',           defaultState: 'optional' },
  { id: 'investor_type',       label: 'Investor type',               defaultState: 'enabled' },
  { id: 'trading_experience',  label: 'Trading experience',          defaultState: 'enabled' },
  { id: 'financial_comfort',   label: 'Financial comfort ranges',    defaultState: 'enabled' },
  { id: 'strategy_builder',    label: 'Strategy builder responses',  defaultState: 'enabled' },
  { id: 'disclosures',         label: 'Disclosures',                 defaultState: 'enabled' },
  { id: 'sms_preference',      label: 'SMS preference',              defaultState: 'optional' },
  { id: 'trusted_contact',     label: 'Trusted contact',             defaultState: 'optional' },

  // ---- Disabled/hidden for Demo and Paper-only ----
  // SSN is the most sensitive — HIDDEN by default; only enabled if a future
  // real-account flow is approved by compliance.
  { id: 'ssn',                       label: 'Social Security Number',          defaultState: 'hidden',   note: 'requires compliance approval' },
  { id: 'tax_id',                    label: 'Tax ID',                          defaultState: 'hidden',   note: 'requires compliance approval' },
  { id: 'date_of_birth',             label: 'Date of birth',                   defaultState: 'hidden',   note: 'requires compliance approval' },
  { id: 'marital_status',            label: 'Marital status',                  defaultState: 'disabled' },
  { id: 'dependents',                label: 'Dependents',                      defaultState: 'disabled' },
  { id: 'backup_tax_withholding',    label: 'Backup tax withholding election', defaultState: 'disabled' },
  { id: 'residential_address',       label: 'Full residential address',        defaultState: 'disabled' },
  { id: 'employer_name',             label: 'Employer name',                   defaultState: 'disabled' },
  { id: 'job_title',                 label: 'Job title',                       defaultState: 'disabled' },
  { id: 'real_brokerage_agreement',  label: 'Real-brokerage account agreement',defaultState: 'disabled' },
  { id: 'live_crypto_enablement',    label: 'Live crypto enablement',          defaultState: 'disabled' },
  { id: 'entity_account_opening',    label: 'Entity account opening',          defaultState: 'disabled' },
  { id: 'joint_account_opening',     label: 'Joint account opening',           defaultState: 'disabled' },
  { id: 'retirement_account_opening',label: 'Retirement account opening',      defaultState: 'disabled' },
];

const REAL_ACCOUNT_FIELDS = new Set([
  'ssn', 'tax_id', 'date_of_birth', 'marital_status', 'dependents',
  'backup_tax_withholding', 'residential_address', 'employer_name',
  'job_title', 'real_brokerage_agreement', 'live_crypto_enablement',
  'entity_account_opening', 'joint_account_opening',
  'retirement_account_opening',
]);

/** Returns the effective state of a field for a given user status. */
export function fieldStateFor(field: FieldSpec, status: UserStatus): FieldState {
  const explicit = field.states?.[status];
  if (explicit) return explicit;
  return field.defaultState;
}

/** Returns the full map of all fields → state for a given user status. */
export function onboardingFieldStates(status: UserStatus): Record<string, FieldState> {
  const out: Record<string, FieldState> = {};
  for (const f of ONBOARDING_FIELDS) out[f.id] = fieldStateFor(f, status);
  return out;
}

/** Is this field part of the real-account / compliance-only set? */
export function isRealAccountField(fieldId: string): boolean {
  return REAL_ACCOUNT_FIELDS.has(fieldId);
}

/** Convenience checks. */
export function isFieldVisible(state: FieldState): boolean {
  return state !== 'hidden';
}
export function isFieldEditable(state: FieldState): boolean {
  return state === 'enabled' || state === 'optional';
}
